import { HTMLAttributes, ReactNode, memo, useState } from "react";
import { IoFilter } from "react-icons/io5";

import { DropdownContainer } from "../DropdownContainer/Dropdown.Container";
import { DropdownPosition } from "../DropdownContainer/Dropdown.Container";
import { DropdownFooter } from "../DropdownContainer/Dropdown.Footer";
import { DropdownOption } from "../DropdownContainer/Dropdown.Option";
import { DropdownOptionItem, DropdownOptionVariant } from "../DropdownContainer/Dropdown.Types";

export enum DropdownVariant {
  DEFAULT = "default",
  WITH_FOOTER = "with_footer"
}

interface FilterDropdownProps extends HTMLAttributes<HTMLDivElement> {
  filterOptions: DropdownOptionItem[];
  selectedFilters?: Record<string, string[]>;
  onChangeFilters: (groupKey: string, filters: string[]) => void;
  onClearAllFilters?: () => void;
  id?: string;
  position?: DropdownPosition;
  dropdownTrigger?: ReactNode;
  dropdownVariant?: DropdownVariant;
}

const BaseFilterDropdown = ({
  filterOptions,
  selectedFilters,
  onChangeFilters,
  onClearAllFilters,
  id,
  position = DropdownPosition.LEFT,
  dropdownTrigger,
  dropdownVariant = DropdownVariant.DEFAULT,
  ...rest
}: FilterDropdownProps) => {
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);

  const activeFiltersTotal = Object.values(selectedFilters || {}).flat().length;

  const handleSelectForGroup = (groupValue: string, optionValue: string, isMultiSelect = true) => {
    const currentFilters = selectedFilters?.[groupValue] ?? [];
    if (isMultiSelect) {
      const nextFilters = currentFilters.includes(optionValue) ? currentFilters.filter((f) => f !== optionValue) : [...currentFilters, optionValue];
      onChangeFilters(groupValue, nextFilters);
    } else {
      const nextFilters = currentFilters.includes(optionValue) ? [] : [optionValue];
      onChangeFilters(groupValue, nextFilters);
    }
  };

  return (
    <DropdownContainer
      isOpen={isDropdownOpen}
      onOpenChange={setIsDropdownOpen}
      position={position}
      id={id}
      classNames={{ dropdown: "w-80", wrapper: "flex items-center justify-center" }}
      footer={
        dropdownVariant === DropdownVariant.WITH_FOOTER ? (
          <DropdownFooter
            totalCount={activeFiltersTotal}
            handleClearAll={() =>
              onClearAllFilters ? onClearAllFilters() : Object.keys(selectedFilters || {}).forEach((key) => onChangeFilters(key, []))
            }
            handleAccept={() => setIsDropdownOpen(false)}
          />
        ) : null
      }
      trigger={
        dropdownTrigger ? (
          dropdownTrigger
        ) : (
          <button
            className="relative cursor-pointer p-1 transition-colors duration-200 ease-in-out hover:text-orange-500 aria-expanded:text-orange-500"
            aria-controls={id}
            aria-expanded={isDropdownOpen}
            type="button"
          >
            <>
              <IoFilter className="size-4" />
              {activeFiltersTotal > 0 && (
                <div className="absolute top-0 right-0 flex size-1.5 items-center justify-center rounded-full bg-orange-500" />
              )}
            </>
          </button>
        )
      }
      {...rest}
    >
      {filterOptions?.length === 0 ? (
        <div className="px-4 py-3 text-center text-sm text-grey-600">No filter options available</div>
      ) : (
        filterOptions.map((group) => {
          const groupFilters = selectedFilters?.[group.value] ?? [];
          const isMultiSelect = group.isMultiSelect !== false;
          const optionVariant = isMultiSelect ? DropdownOptionVariant.WITH_CHECKBOX : DropdownOptionVariant.DEFAULT;

          return (
            <DropdownOption
              key={group.value}
              onSelect={(value: string) => handleSelectForGroup(group.value, value, isMultiSelect)}
              option={{
                ...group,
                variant: optionVariant,
                options: group.options?.map((opt) => ({
                  ...opt,
                  variant: optionVariant
                }))
              }}
              isOptionSelected={(value: string) => groupFilters.includes(value)}
            />
          );
        })
      )}
    </DropdownContainer>
  );
};

export const FilterDropdown = memo(BaseFilterDropdown);
